/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllRoutes } from '../modules/routes/stores/slice';
import { DATE_BACKEND_FORMAT, PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../constants';
import { doGetRoutes, doDeleteRoute } from '../modules/routes/stores/thunk';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import RoutesTable from '../components/Tables/RoutesTable';
import RoutesFilter from '../components/Forms/Filters/RoutesFilter';
import RouteDateQuickFilter from '../components/Forms/Filters/RouteDateQuickFilter';
import { downloadRoutesCsv, downloadRoutePausesCsv } from '../utils/routeCsvExport';
import { getRoutes } from '../modules/routes/api';
import { Button, Input, Popover, Badge, Tooltip, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import DeleteRouteModal from '../components/Modals/DeleteRouteModal';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const RoutesPage = () => {
  const { t } = useTranslation('routes');
  const { t: tc } = useTranslation('common');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const routes = useSelector(selectAllRoutes);

  const { metadata, isFetching } = useSelector((state) => state.route);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const tableConfigRef = useRef({});
  const [filterOpen, setFilterOpen] = useState(false);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  const [configPageSize, setConfigPageSize] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ovo-table-config-routes-columns') || '{}').pageSize || PAGE_SIZE;
    } catch {
      return PAGE_SIZE;
    }
  });
  const itemsPerPage = query.pageSize || configPageSize;
  const pagination = {
    current: currentPage || 1,
    pageSize: Number(itemsPerPage),
    locale: { items_per_page: tc('itemsPerPage', { entity: t('title') }) },
    total: metadata.totalItems || routes.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('title') }),
  };

  useEffect(() => {
    if (query.q) {
      setSearchValue(query.q);
    }
  }, []);

  useEffect(() => {
    dispatch(doGetRoutes({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.locale;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = 1;
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetRoutes({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleTableChange = (paginationParams, _filters, sorter) => {
    const newParams = {
      ...query,
      current: paginationParams.current,
      pageSize: paginationParams.pageSize,
    };

    // Map Ant Design sorter to backend params
    if (sorter && sorter.field && sorter.order) {
      newParams.sortField = sorter.field;
      newParams.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      // Sort cleared — remove sort params
      delete newParams.sortField;
      delete newParams.sortOrder;
    }

    handleFilter(newParams, true);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  // Quick date presets (Tomorrow / Today / Yesterday / This week / All).
  // Merge into the current query so existing filters (driver, status, search,
  // sort) survive; "All" clears the date window. URL-synced via handleFilter.
  const handleQuickDate = ({ dateFrom, dateTo }) => {
    const next = { ...query, current: 1 };
    if (dateFrom) next.dateFrom = dateFrom;
    else delete next.dateFrom;
    if (dateTo) next.dateTo = dateTo;
    else delete next.dateTo;
    handleFilter(next, true);
  };

  const activeFilterCount = [query.dateFrom || query.dateTo, query.driver, query.status, query.partnerId].filter(
    Boolean,
  ).length;

  // OVO-126 follow-up: DISPATCHED routes are now deletable (sent to the
  // driver but not started). Only once the driver has started/finished do
  // we protect the route (real execution data).
  const NON_DELETABLE_STATUSES = ['IN_PROGRESS', 'COMPLETED'];
  const [isActionOpen, setIsActionOpen] = useState(false);
  // OVO-126 — delete confirmation modal (single + bulk) with the
  // "also delete orders" checkbox. `mode` is 'single' | 'bulk'.
  const [deleteModal, setDeleteModal] = useState({ open: false, mode: 'single', record: null });

  // One block per driver, chronological inside it — the timesheet order.
  const byDriverThenDate = (a, b) =>
    (a.driver?.name || '').localeCompare(b.driver?.name || '') ||
    String(a.date || '').localeCompare(String(b.date || '')) ||
    a.id - b.id;

  const exportPauses = (list) => {
    const count = downloadRoutePausesCsv(list);
    if (count) message.success(t('exportPausesSuccess', { count }));
    else message.info(t('exportPausesEmpty'));
  };

  const handleExportRoutes = () => {
    if (isActionOpen) setIsActionOpen(false);
    const selectedRoutes = routes.filter((r) => selectedRowKeys?.includes(r.id));
    if (!selectedRoutes.length) return;
    downloadRoutesCsv(selectedRoutes, { hr: true });
    message.success(t('exportSuccess', { count: selectedRoutes.length }));
  };

  const handleExportSelectedPauses = () => {
    if (isActionOpen) setIsActionOpen(false);
    const selectedRoutes = routes.filter((r) => selectedRowKeys?.includes(r.id));
    if (!selectedRoutes.length) return;
    exportPauses([...selectedRoutes].sort(byDriverThenDate));
  };

  // #134 — monthly HR export: EVERY route matching the current filters (not
  // just the page on screen), with the timesheet columns. Typical use: quick
  // range "Last month" (+ a driver in the Filter drawer) → Export. The pauses
  // export reads the same routes and writes one row per pause.
  const EXPORT_PAGE_SIZE = 100;
  const EXPORT_MAX_ROUTES = 3000;
  const [exportingAll, setExportingAll] = useState(null); // null | 'routes' | 'pauses'
  const exportAllFiltered = async (kind, write) => {
    setExportingAll(kind);
    try {
      // eslint-disable-next-line no-unused-vars
      const { current, pageSize, ...filters } = query;
      const all = [];
      let page = 1;
      let total = Infinity;
      while (all.length < total) {
        const { response, error } = await getRoutes({ ...filters, page, take: EXPORT_PAGE_SIZE });
        if (error) throw error;
        const batch = response?.routes || [];
        total = response?.pagination?.totalItems ?? batch.length;
        if (total > EXPORT_MAX_ROUTES) {
          message.warning(t('exportAllTooMany', { count: total, max: EXPORT_MAX_ROUTES }));
          return;
        }
        all.push(...batch);
        if (batch.length < EXPORT_PAGE_SIZE) break;
        page += 1;
      }
      if (!all.length) {
        message.info(t('exportAllEmpty'));
        return;
      }
      write(all.sort(byDriverThenDate));
    } catch (e) {
      message.error(e?.message || t('exportAllFailed'));
    } finally {
      setExportingAll(null);
    }
  };
  const handleExportAllRoutes = () =>
    exportAllFiltered('routes', (all) => {
      downloadRoutesCsv(all, { hr: true });
      message.success(t('exportSuccess', { count: all.length }));
    });
  const handleExportAllPauses = () => exportAllFiltered('pauses', exportPauses);

  const handleBulkDeleteRoutes = async (deleteOrders = false, forceConfirmed = false) => {
    const selected = routes.filter((r) => selectedRowKeys.includes(r.id));
    const deletableRoutes = selected.filter((r) => !NON_DELETABLE_STATUSES.includes(r.status?.toUpperCase()));
    const blockedRoutes = selected.filter((r) => NON_DELETABLE_STATUSES.includes(r.status?.toUpperCase()));

    let deleted = 0;
    for (const route of deletableRoutes) {
      try {
        await dispatch(doDeleteRoute({ id: route.id, deleteOrders })).unwrap();
        deleted++;
      } catch {
        // continue with others
      }
    }
    // Force-delete (typed confirmation matched): permanent, orders included —
    // the backend enforces both regardless of the checkbox.
    if (forceConfirmed) {
      for (const route of blockedRoutes) {
        try {
          await dispatch(doDeleteRoute({ id: route.id, deleteOrders: true, force: true })).unwrap();
          deleted++;
        } catch {
          // continue with others
        }
      }
    }

    if (deleted > 0) {
      message.success(t('routesDeleted', { count: deleted }));
    }
    if (!forceConfirmed && blockedRoutes.length > 0) {
      message.warning(t('dispatchedCannotDelete', { count: blockedRoutes.length }));
    }

    setSelectedRowKeys([]);
    dispatch(doGetRoutes({ page: currentPage, take: itemsPerPage, ...query }));
  };

  const handleDeleteRoute = async (record, deleteOrders = false, force = false) => {
    try {
      await dispatch(doDeleteRoute({ id: record.id, deleteOrders: force ? true : deleteOrders, force })).unwrap();
      message.success(t('routeDeleted', { id: record.id }));
      // Refresh the routes list
      dispatch(doGetRoutes({ page: currentPage, take: itemsPerPage, ...query }));
    } catch (error) {
      message.error(error?.message || t('failedToDelete'));
    }
  };

  // OVO-126 — single entry point the modal calls on confirm. `forceConfirmed`
  // is true only when the operator typed the blocked route ids in the modal.
  const handleConfirmDelete = async (deleteOrders, forceConfirmed = false) => {
    const { mode, record } = deleteModal;
    setDeleteModal({ open: false, mode, record: null });
    if (mode === 'bulk') {
      setIsActionOpen(false);
      await handleBulkDeleteRoutes(deleteOrders, forceConfirmed);
    } else if (record) {
      const blocked = NON_DELETABLE_STATUSES.includes(record.status?.toUpperCase());
      if (blocked && !forceConfirmed) return; // button is disabled in this state anyway
      await handleDeleteRoute(record, deleteOrders, blocked);
    }
  };

  const withAction = (record) => {
    const isDeletable = !NON_DELETABLE_STATUSES.includes(record.status?.toUpperCase());
    return (
      <div className="actions">
        <Link to={`/admin/route-plans?date=${moment(record.date).format(DATE_BACKEND_FORMAT)}`}>
          <Button type="link" className="edit">
            <ReactSVG className="icon" src="/icons/route-plans.svg" />
          </Button>
        </Link>
        {/* Force-delete: blocked (started/completed) routes open the same
            modal, which requires typing the route id before enabling Delete. */}
        <Button
          type="link"
          className="more"
          title={isDeletable ? undefined : t('forceDeleteRowHint')}
          onClick={() => setDeleteModal({ open: true, mode: 'single', record })}
        >
          <ReactSVG className="icon" src="/icons/more.svg" />
        </Button>
      </div>
    );
  };

  return (
    <div className="RoutesPage Content">
      <div className="ContentHeader">
        <div className="HeaderTitle">{t('title')}</div>
        <div className="HeaderToolBar"></div>
      </div>
      <div className="page-header-table">
        <div className="search">
          <Input
            onChange={handleChangeSearch}
            onBlur={handleSearch}
            placeholder={tc('search')}
            onKeyDown={handleKeyDown}
            suffix={<img src="/icons/search.svg" />}
            disabled={isFetching}
            value={searchValue}
          />
        </div>
        <div className="quick">
          <RouteDateQuickFilter dateFrom={query.dateFrom} dateTo={query.dateTo} onChange={handleQuickDate} />
        </div>
        <div className="buttons">
          {selectedRowKeys.length > 0 && (
            <Popover
              open={selectedRowKeys?.length > 0 && isActionOpen}
              onOpenChange={(open) => setIsActionOpen(open)}
              trigger="click"
              placement="bottom"
              overlayClassName="OrdersPagePopoverAction"
              content={
                <div className="buttons">
                  <Button type="link" onClick={handleExportRoutes}>
                    {t('exportCsv')}
                  </Button>
                  <Button type="link" onClick={handleExportSelectedPauses} data-testid="routes-export-selected-pauses">
                    {t('exportPauses')}
                  </Button>
                  <Button
                    type="link"
                    onClick={() => {
                      setIsActionOpen(false);
                      setDeleteModal({ open: true, mode: 'bulk', record: null });
                    }}
                  >
                    {tc('delete')}
                  </Button>
                </div>
              }
            >
              <Button className="FilterButton ButtonFilter">
                {selectedRowKeys?.length > 0 && <div className="count">{selectedRowKeys.length}</div>}
                {tc('action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
              </Button>
            </Popover>
          )}

          <Tooltip title={t('exportAllHint')}>
            <Button
              className="FilterButton ButtonFilter"
              icon={<DownloadOutlined />}
              loading={exportingAll === 'routes'}
              disabled={!metadata.totalItems || exportingAll === 'pauses'}
              onClick={handleExportAllRoutes}
              data-testid="routes-export-all"
            >
              {t('exportAll', { count: metadata.totalItems || 0 })}
            </Button>
          </Tooltip>

          <Tooltip title={t('exportPausesHint')}>
            <Button
              className="FilterButton ButtonFilter"
              icon={<DownloadOutlined />}
              loading={exportingAll === 'pauses'}
              disabled={!metadata.totalItems || exportingAll === 'routes'}
              onClick={handleExportAllPauses}
              data-testid="routes-export-pauses"
            >
              {t('exportPauses')}
            </Button>
          </Tooltip>

          <Badge count={activeFilterCount} size="small" offset={[-5, 0]}>
            <Button className="FilterButton ButtonFilter" onClick={() => setFilterOpen(true)}>
              {tc('filter')}
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
          </Badge>
          <Button type="text" onClick={() => tableConfigRef.current?.openDrawer?.()}>
            <ReactSVG className="icon" src="/icons/settings.svg" />
          </Button>
        </div>
      </div>

      <RoutesFilter open={filterOpen} onClose={() => setFilterOpen(false)} query={query} onApply={handleFilter} />
      <div className="ContentMain">
        <RoutesTable
          dataSource={routes}
          pagination={pagination}
          rowKey="id"
          isFetching={isFetching}
          onSelectChange={onSelectChange}
          configRef={tableConfigRef}
          onPageSizeChange={(size) => {
            setConfigPageSize(size);
            handleFilter({ ...query, pageSize: size, current: 1 }, true);
          }}
          selectedRowKeys={selectedRowKeys}
          withAction={withAction}
          onChange={handleTableChange}
        />
      </div>

      <DeleteRouteModal
        open={deleteModal.open}
        mode={deleteModal.mode}
        routeId={deleteModal.mode === 'single' ? deleteModal.record?.id : undefined}
        count={deleteModal.mode === 'bulk' ? selectedRowKeys.length : 1}
        // Warn when a dispatched route is in the deletion set.
        dispatched={
          deleteModal.mode === 'single'
            ? deleteModal.record?.status?.toUpperCase() === 'DISPATCHED'
            : routes.some((r) => selectedRowKeys.includes(r.id) && r.status?.toUpperCase() === 'DISPATCHED')
        }
        forceRouteIds={
          deleteModal.mode === 'single'
            ? NON_DELETABLE_STATUSES.includes(deleteModal.record?.status?.toUpperCase())
              ? [deleteModal.record.id]
              : []
            : routes
                .filter(
                  (r) => selectedRowKeys.includes(r.id) && NON_DELETABLE_STATUSES.includes(r.status?.toUpperCase()),
                )
                .map((r) => r.id)
        }
        onCancel={() => setDeleteModal((s) => ({ ...s, open: false }))}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default RoutesPage;
